import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import Box from "@mui/material/Box";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";

import { TCatalogEntities } from "types";
import Pipeline from "../Pipeline";
import { sortAsc } from "../helper";

import List from "../../List";
import Filter from "../../Filter";

import LocationReducer from "./LocationReducer";
import LocationIndicator from "./LocationIndicator";

interface IFilter {
  entities: TCatalogEntities;
  value: number[];
  dispatchFilter: any;
}

export const SideFilter: React.FC<IFilter> = ({
  entities = {},
  value = [],
  dispatchFilter = () => null,
}): JSX.Element | null => {
  const { formatMessage: t } = useIntl();

  if (!entities.location) return null;

  const items = Object.values(entities.location);
  const itemsCount = items.length;

  const setValueHandler = (value: any) => {
    // Check if all variants are selected
    const newValue = value.length === itemsCount ? [] : value;
    dispatchFilter({ type: "UPDATE_FILTER", payload: { location: newValue } });
  };

  const options = items.map(({ id, name }) => ({
    value: id,
    title: t({ id: "location." + id + ".name", defaultMessage: name }),
  }));

  const all = {
    title: t({ id: "common.alle", defaultMessage: "Alle" }),
  };

  const Component = (
    <Box sx={{ width: 1 }}>
      <List {...{ value, options, setValueHandler, all }} />
    </Box>
  );
  return (
    <Filter
      component={Component}
      title={
        <FormattedMessage
          id="filter.location.label"
          defaultMessage="Standort"
        />
      }
    />
  );
};

export const SimpleFilter: React.FC<IFilter> = ({
  entities = {},
  value = [],
  dispatchFilter = () => null,
}): JSX.Element | null => {
  const { formatMessage: t } = useIntl();
  if (!entities.location) return null;

  const items = Object.values(entities.location);

  const setValueHandler = (event: SelectChangeEvent) => {
    // Check if all variants are selected
    const value = parseInt(event.target.value);
    const newValue = value === 0 ? [] : [value];
    dispatchFilter({ type: "UPDATE_FILTER", payload: { location: newValue } });
  };

  const options = items
    .map(({ id, name }) => ({
      value: id,
      title: t({ id: "location." + id + ".name", defaultMessage: name }),
    }))
    .sort(sortAsc);
  const all = {
    title: t({ id: "common.alle", defaultMessage: "Alle" }),
  };

  return (
    <FormControl size="small">
      <InputLabel id="filter-location-simple-label">
        <FormattedMessage
          id="filter.location.label"
          defaultMessage="Standort"
        />
      </InputLabel>
      <Select
        labelId="filter-location-simple-label"
        id="filter-location-simple"
        value={value.length ? value[0].toString() : "0"}
        label={t({ id: "filter.location.label", defaultMessage: "Standort" })}
        onChange={setValueHandler}
      >
        <MenuItem value={0}>{all.title}</MenuItem>
        {options.map(({ value, title }) => (
          <MenuItem key={"location-" + value} value={value}>
            {title}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

interface IContainer {
  Pipe?: any;
  variant?: string;
}

const Container: React.FC<IContainer> = ({
  Pipe = Pipeline,
  variant = "side",
}) => {
  const Filter = variant === "side" ? SideFilter : SimpleFilter;
  return (
    <Pipe
      {...{
        type: "location",
        defaultValue: [],
        Component: Filter,
        Reducer: LocationReducer,
        Indicator: LocationIndicator,
      }}
    />
  );
};

export default Container;
